import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useApiQuery } from '../use-api.js';
import { LoopSelect } from '../components/LoopSelect.js';
import { MetricCard } from '../components/MetricCard.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import type { MetricStatus } from '../components/MetricSparkline.js';
import { SCORECARD_DISPLAY_NAME, SCORECARD_METRIC_HELP, metricFormat, scorecardWindowLabel } from '../lib/scorecard-format.js';
import { ConceptHelpIcon } from '../components/ConceptHelpIcon.js';
import { SPARKLINE_KIND, projectSeries, type DailyMetricsPoint } from './velocity-scorecard.projection.js';

// Empty-string sentinel mirrors weekly-digest.tsx — "all orgs" is the default
// landing so the user sees the portfolio view before drilling into a single
// org. Once an org is picked from the dropdown the URL pins it via ?org=.
const ALL_ORGS = '';

function isValidOrg(v: string | null, orgs: string[]): v is string {
  if (v === null) return false;
  if (v === ALL_ORGS) return true;
  return orgs.includes(v);
}

interface ScorecardMetric {
  metric: string;
  baseline: number;
  current: number;
  delta: number;
  color: MetricStatus;
  note?: string | null;
}

interface IncidentBotApprovedLinkDetail {
  incident_id: string;
  incident_title: string;
  source: 'confluence-postmortem' | 'pagerduty-statuspage';
  source_url: string;
  pr_org: string;
  pr_repo: string;
  pr_number: number;
  confidence: number;
  reason: string;
}

interface TokenDailyPoint {
  date: string;
  tokens_in_total: number;
  // Optional for back-compat with API builds that pre-date the instrumented
  // denominator (the card only reads cache_read_share, which both emit).
  tokens_in_instrumented?: number;
  input_tokens_excl_cache: number;
  cache_read_tokens: number;
  cache_read_share: number;
}

interface ScorecardWindow {
  from: string;
  to: string;
  days: number;
  clamped: boolean;
}

interface ScorecardResponse {
  // 'all' when no org is selected, otherwise the picked org's login.
  org: string;
  metrics: ScorecardMetric[];
  daily: DailyMetricsPoint[];
  // Optional for back-compat with API builds that pre-date the token-usage
  // card — older deploys omit these and the card renders an empty state.
  token_daily?: TokenDailyPoint[];
  cache_read_share?: number | null;
  tokens_in_per_day_baseline?: number | null;
  cache_read_share_baseline?: number | null;
  incidents_linked_to_bot_approved: number;
  incidents_linked_to_bot_approved_detail: IncidentBotApprovedLinkDetail[];
  // The clamped window the server actually served — the source of truth for
  // what period is displayed, since the server clamps out-of-range requests.
  // Optional for back-compat with API builds that pre-date the period picker,
  // matching the convention every other added field here follows; the readout
  // and the tile captions degrade rather than throwing.
  window?: ScorecardWindow;
}

/** `anchor - 29 days`, matching the "Last 30 days" preset. `anchor` is the
 *  effective `to` (see below), not necessarily today. */
function defaultFrom(anchor: string): string {
  return new Date(Date.parse(`${anchor}T00:00:00.000Z`) - 29 * 24 * 60 * 60 * 1000)
    .toISOString()
    .slice(0, 10);
}

/** A bare `?from=`/`?to=` reaches us as `''`, not `null` — the date input
 *  mid-clear, same case resolveScorecardWindow's blankAsAbsent absorbs
 *  server-side. Treat it as absent here too, so the empty value never
 *  forwards into the request or into PeriodPicker's controlled props. */
function blankAsAbsent(v: string | null): string | undefined {
  return v === '' ? undefined : (v ?? undefined);
}

// Re-use the label map from the shared lib so tests can import it without
// pulling in the browser-env dependencies from this route module.
const DISPLAY_NAME = SCORECARD_DISPLAY_NAME;

export function VelocityScorecard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [expanded, setExpanded] = useState(false);

  const enterpriseOrgs = useApiQuery<{ orgs: Array<{ login: string; opted_in: boolean }> }>(
    ['enterprise-orgs'],
    '/api/enterprise-orgs'
  );
  // Filter to opted-in orgs only — picking a non-opted-in org reliably 403s at
  // /api/scorecard (fetchAllowedOrgs gates on installed ∩ opted-in). Matches
  // cost.tsx's pattern so the dropdown experiences are consistent.
  const orgOptions: string[] = (enterpriseOrgs.data?.orgs ?? [])
    .filter((o) => o.opted_in)
    .map((o) => o.login)
    .sort();

  const rawOrg = searchParams.get('org');
  const org: string = isValidOrg(rawOrg, orgOptions) ? rawOrg : ALL_ORGS;
  const isAllOrgs = org === ALL_ORGS;

  function setOrg(newOrg: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('org', newOrg);
      return next;
    });
  }

  // Selection lives in the URL alongside org, so a link to a period is
  // shareable. Defaults to the trailing 30 days when unset — mirroring
  // resolveScorecardWindow's own defaulting: a missing `from` is derived from
  // the effective `to` (today, or `to` clamped to today if it's in the
  // future), not from today directly. Otherwise a link carrying only `to`
  // (e.g. ?to=2026-08-31) renders a shorter window here than the same params
  // produce server-side, since the API derives `from` from `to` too.
  const today = new Date().toISOString().slice(0, 10);
  const rawFrom = blankAsAbsent(searchParams.get('from'));
  const rawTo = blankAsAbsent(searchParams.get('to'));
  const effectiveTo = rawTo !== undefined && rawTo <= today ? rawTo : today;
  const from = rawFrom ?? defaultFrom(effectiveTo);
  const to = rawTo ?? today;

  function setRange(next: { from: string; to: string }) {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set('from', next.from);
      p.set('to', next.to);
      return p;
    });
  }

  // Omit ?org= for all-orgs to keep the network-tab URL clean; the API treats
  // missing/empty org as all-orgs aggregation.
  const params = new URLSearchParams({ from, to });
  if (!isAllOrgs) params.set('org', org);
  const scorecardUrl = `/api/scorecard?${params.toString()}`;
  const { data, isLoading, error } = useApiQuery<ScorecardResponse>(
    ['scorecard', org, from, to],
    scorecardUrl
  );

  // Tile caption + sparkline aria-label reflect the *served* window (the
  // server clamps), not the requested one. 'Last Nd' is reserved for windows
  // that actually end today — see scorecardWindowLabel.
  const servedWindow = data?.window;
  const windowLabel = data ? scorecardWindowLabel(servedWindow, today) : 'Selected';

  // Council tokens/day: average daily total over the window vs the pinned
  // steady-state baseline (established post-caching level). Lower is better, so
  // staying at/below the steady-state level is green and creeping above it
  // (a regression) is red.
  const tokenPts = data?.token_daily ?? [];
  const tokensAvgPerDay = tokenPts.length
    ? Math.round(tokenPts.reduce((sum, p) => sum + p.tokens_in_total, 0) / tokenPts.length)
    : null;
  const tokensBaseline = data?.tokens_in_per_day_baseline ?? null;
  const tokensDelta = tokensAvgPerDay != null && tokensBaseline != null ? tokensAvgPerDay - tokensBaseline : null;
  const tokensStatus: MetricStatus | undefined =
    tokensDelta == null ? undefined : tokensDelta < 0 ? 'green' : tokensDelta > 0 ? 'red' : undefined;

  // Cache-read share: compared against the pinned steady-state baseline (the
  // established post-caching utilization level). Higher is better — climbing
  // above the steady-state level is green, slipping below it is red. A null
  // share (no instrumented SDK-path traffic in the window) or a null baseline
  // (older API build, or no baseline-window activity) drops the delta and
  // renders the plain level rather than a hollow comparison.
  const cacheShare = data?.cache_read_share ?? null;
  const cacheShareBaseline = data?.cache_read_share_baseline ?? null;
  const cacheShareDelta =
    cacheShare != null && cacheShareBaseline != null ? cacheShare - cacheShareBaseline : null;
  const cacheShareStatus: MetricStatus | undefined =
    cacheShareDelta == null ? undefined : cacheShareDelta > 0 ? 'green' : cacheShareDelta < 0 ? 'red' : undefined;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--loop-space-100)', marginBottom: 16 }}>
        <h2 style={{ fontSize: '1.125rem', fontWeight: 664, margin: 0, color: 'var(--loop-neutral-1100)' }}>Velocity Scorecard</h2>
        <ConceptHelpIcon slug="velocity-scorecard" />
      </div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
        <LoopSelect
          id="scorecard-org-select"
          label="Org"
          value={org}
          onChange={setOrg}
          disabled={enterpriseOrgs.isLoading}
          options={[
            { value: ALL_ORGS, label: 'All orgs' },
            ...orgOptions.map((o) => ({ value: o, label: o })),
          ]}
        />
        <PeriodPicker from={from} to={to} today={today} onChange={setRange} />
      </div>

      {isLoading && <p style={{ color: 'var(--loop-neutral-700)', fontSize: '0.875rem' }}>Loading…</p>}
      {error && <p className="error">Error: {error instanceof Error ? error.message : String(error)}</p>}

      {data && (
        <>
          {servedWindow && (
            <p style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)', margin: '0 0 16px' }}>
              {/* The clamp note deliberately does not repeat the horizon date:
                  this same line already prints it as window.from, and two
                  copies of the constant is exactly how one of them goes stale. */}
              {servedWindow.from} → {servedWindow.to} · {servedWindow.days} days
              {servedWindow.clamped && ' · clamped to the start of Diff Vader data'}
            </p>
          )}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 16,
              alignItems: 'stretch',
            }}
          >
            {(data.metrics ?? []).map(m => {
              const sparklineKind = SPARKLINE_KIND[m.metric];
              const sparkline = sparklineKind != null
                ? { data: projectSeries(m.metric, data.daily ?? []), kind: sparklineKind }
                : undefined;
              return (
                <MetricCard
                  key={m.metric}
                  name={DISPLAY_NAME[m.metric] ?? m.metric}
                  baseline={m.baseline}
                  current={m.current}
                  delta={m.delta}
                  status={m.color}
                  format={metricFormat(m.metric)}
                  helpText={SCORECARD_METRIC_HELP[m.metric]}
                  sparkline={sparkline}
                  windowLabel={windowLabel}
                  note={m.note}
                />
              );
            })}

            {/* Token-usage cards (prompt-caching observability). token_daily is
                its own response series, not part of `daily`, so the line is
                built inline rather than via projectSeries. The total line is
                backfilled across the full window; cache-read share starts at
                the DV_DISPATCH=sdk rollout (older runs lack the breakdown). */}
            <MetricCard
              key="tokens_in_per_day"
              name={DISPLAY_NAME['tokens_in_per_day'] ?? 'Council tokens / day'}
              // Baseline = avg/day over the pinned steady-state window; current
              // = avg/day over the selected range. Both per-day averages so they
              // compare directly regardless of how wide the range is; a drop
              // below baseline (negative delta) is the win.
              baseline={tokensBaseline}
              current={tokensAvgPerDay}
              delta={tokensDelta}
              status={tokensStatus}
              format={metricFormat('tokens_in_per_day')}
              helpText={SCORECARD_METRIC_HELP['tokens_in_per_day']}
              sparkline={{
                data: (data.token_daily ?? []).map(p => ({ date: p.date, value: p.tokens_in_total })),
                kind: 'line',
              }}
              windowLabel={windowLabel}
              note={null}
            />
            <MetricCard
              key="cache_read_share"
              name={DISPLAY_NAME['cache_read_share'] ?? 'Cache-read share'}
              baseline={cacheShareBaseline}
              current={cacheShare}
              delta={cacheShareDelta}
              status={cacheShareStatus}
              format={metricFormat('cache_read_share')}
              helpText={SCORECARD_METRIC_HELP['cache_read_share']}
              sparkline={{
                data: (data.token_daily ?? []).map(p => ({ date: p.date, value: p.cache_read_share })),
                kind: 'line',
              }}
              windowLabel={windowLabel}
              note={null}
            />

            <button
              type="button"
              onClick={() => setExpanded(v => !v)}
              className="card"
              style={{
                marginBottom: 0,
                textAlign: 'left',
                cursor: 'pointer',
                background: 'var(--loop-neutral-0)',
                font: 'inherit',
                color: 'inherit',
                display: 'flex',
                flexDirection: 'column',
              }}
              aria-expanded={expanded}
              aria-controls="incidents-bot-approved-detail"
            >
              <div style={{ fontSize: '0.875rem', fontWeight: 528, color: 'var(--loop-neutral-900)', marginBottom: 'var(--loop-space-100)' }}>
                Incidents linked to bot-approved PRs
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12 }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>Count</div>
                  <div style={{ fontSize: '1.5rem', fontWeight: 664, color: 'var(--loop-neutral-1100)' }}>
                    {data.incidents_linked_to_bot_approved}
                  </div>
                </div>
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: '0.75rem',
                    color: 'var(--loop-blue-700)',
                  }}
                >
                  {expanded ? 'Hide details ▴' : 'Show details ▾'}
                </span>
              </div>
            </button>
          </div>

          {expanded && (
            <div
              id="incidents-bot-approved-detail"
              className="card"
              style={{ marginTop: 16 }}
            >
              {data.incidents_linked_to_bot_approved_detail.length === 0 ? (
                <p style={{ color: 'var(--loop-neutral-600)', fontSize: '0.875rem', margin: 0 }}>
                  {servedWindow
                    ? `No (incident, bot-approved PR) pairs between ${servedWindow.from} and ${servedWindow.to}.`
                    : 'No (incident, bot-approved PR) pairs in the selected range.'}
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: 'var(--loop-neutral-700)' }}>
                      <th style={{ padding: '4px 8px' }}>Incident</th>
                      {/* Org column surfaces in all-orgs view so the reader
                          can disambiguate two PRs with the same number from
                          different orgs. Hidden in per-org view to save width. */}
                      {isAllOrgs && <th style={{ padding: '4px 8px' }}>Org</th>}
                      <th style={{ padding: '4px 8px' }}>PR</th>
                      <th style={{ padding: '4px 8px' }}>Confidence</th>
                      <th style={{ padding: '4px 8px' }}>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.incidents_linked_to_bot_approved_detail.map((row, idx) => (
                      <tr
                        key={`${row.incident_id}#${row.pr_org}/${row.pr_repo}#${row.pr_number}#${idx}`}
                        style={{ borderTop: '1px solid var(--loop-neutral-200)' }}
                      >
                        <td style={{ padding: '6px 8px' }}>
                          <a
                            href={row.source_url}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: 'var(--loop-blue-700)' }}
                          >
                            {row.incident_title || row.incident_id}
                          </a>
                          <div style={{ fontSize: '0.75rem', color: 'var(--loop-neutral-600)' }}>
                            {row.source}
                          </div>
                        </td>
                        {isAllOrgs && (
                          <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                            {row.pr_org}
                          </td>
                        )}
                        <td style={{ padding: '6px 8px' }}>
                          <Link
                            to={`/repos/${row.pr_org}/${row.pr_repo}/pr/${row.pr_number}`}
                            style={{ color: 'var(--loop-blue-700)' }}
                          >
                            {row.pr_org}/{row.pr_repo}#{row.pr_number}
                          </Link>
                        </td>
                        <td style={{ padding: '6px 8px' }}>{row.confidence}</td>
                        <td style={{ padding: '6px 8px', color: 'var(--loop-neutral-700)' }}>{row.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

        </>
      )}
    </div>
  );
}
